import React, { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ListGuestsPage from './pages/ListGuestsPage';
import MessagePage from './pages/MessagePage';
import SendWhatsAppMessagePage from './pages/SendWhatsAppMessagePage';
import AddGuestPage from './pages/AddGuestPage';
import ScheduledMessagesPage from './pages/ScheduledMessagesPage';
import AdminPage from './pages/AdminPage';
import InboundMessagesPage from './pages/InboundMessagesPage';
import MediaPage from './pages/MediaPage';
import KnowledgeManagementPage from './pages/KnowledgeManagementPage';
import DeleteEventComponentsPage from './pages/DeleteEventComponentsPage';
import AIConfigPage from './pages/AIConfigPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import AddMePage from './pages/AddMePage';
import SelfRegistrationSettingsPage from './pages/SelfRegistrationSettingsPage';
import EventManagementPage from './pages/EventManagementPage';
import TemplateManagementPage from './pages/TemplateManagementPage';
import BulkAddGuestsPage from './pages/BulkAddGuestsPage';
import ProfilePage from './pages/ProfilePage';
import HeyDJPage from './pages/HeyDJPage';
import HeyDJPublicPage from './pages/HeyDJPublicPage';
import HeyDJRequestPage from './pages/HeyDJRequestPage';
import EventsPage from './pages/EventsPage';
import UserGuidePage from './pages/UserGuidePage';
import AITestPage from './pages/AITestPage';
import { CONFIG } from './config';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [userName, setUserName] = useState('');
  const [activeEventId, setActiveEventId] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [allowedChannels, setAllowedChannels] = useState('both');
  const [customGroups, setCustomGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const checkAuthStatus = async () => {
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/auth-check`, {
        credentials: 'include',
      });

      if (resp.ok) {
        setIsAuthenticated(true);

        // Fetch user groups and active event in parallel
        try {
          const [userInfoResp, eventResp] = await Promise.all([
            fetch(`${CONFIG.apiBaseUrl}/user-info`, { credentials: 'include' }),
            fetch(`${CONFIG.apiBaseUrl}/active-event`, { credentials: 'include' }),
          ]);
          if (userInfoResp.ok) {
            const userInfo = await userInfoResp.json();
            setUserGroups(userInfo.groups || []);
            setUserName(userInfo.name || '');
          }
          if (eventResp.ok) {
            const eventData = await eventResp.json();
            setActiveEventId(eventData.activeEventId || null);
            setAvailableEvents(eventData.availableEvents || []);
            setAllowedChannels(eventData.allowedChannels || 'both');
            setCustomGroups(eventData.customGroups || null);
          }
        } catch (err) {
          console.error('Failed to fetch user info:', err);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };

  const handleEventSwitch = async (eventId) => {
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/set-active-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId }),
        credentials: 'include',
      });
      if (resp.ok) {
        setActiveEventId(eventId);
        const switched = availableEvents.find(e => e.event_id === eventId);
        setAllowedChannels(switched?.allowed_channels || 'both');
        setCustomGroups(switched?.custom_groups || null);
      }
    } catch (err) {
      console.error('Failed to switch event:', err);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  const isAdmin = userGroups.includes('admin');

  return (
    <div className="app-container">
      <Navigation
        currentPath={currentPath}
        onNavigate={navigate}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        userName={userName}
        activeEventId={activeEventId}
        availableEvents={availableEvents}
        allowedChannels={allowedChannels}
      />

      <main className="main-content">
        {(currentPath === '/' || currentPath === '/home') && (
          <HomePage
            isAuthenticated={isAuthenticated}
            activeEventId={activeEventId}
            availableEvents={availableEvents}
            onEventSwitch={handleEventSwitch}
          />
        )}
        
        {currentPath === '/list-guests' && isAuthenticated && <ListGuestsPage key={activeEventId} allowedChannels={allowedChannels} customGroups={customGroups} />}
        {currentPath === '/list-guests' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/message' && isAuthenticated && <MessagePage key={activeEventId} customGroups={customGroups} />}
        {currentPath === '/message' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/whatsapp-message' && isAuthenticated && <SendWhatsAppMessagePage key={activeEventId} customGroups={customGroups} isAdmin={isAdmin} />}
        {currentPath === '/whatsapp-message' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/scheduled-messages' && isAuthenticated && <ScheduledMessagesPage key={activeEventId} customGroups={customGroups} />}
        {currentPath === '/scheduled-messages' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/add-guest' && isAuthenticated && <AddGuestPage key={activeEventId} allowedChannels={allowedChannels} customGroups={customGroups} />}
        {currentPath === '/add-guest' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/bulk-add-guests' && isAuthenticated && isAdmin && <BulkAddGuestsPage key={activeEventId} />}
        {currentPath === '/bulk-add-guests' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/bulk-add-guests' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/profile' && isAuthenticated && (
          <ProfilePage
            activeEventId={activeEventId}
            availableEvents={availableEvents}
            onEventSwitch={handleEventSwitch}
          />
        )}
        {currentPath === '/profile' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin' && isAuthenticated && isAdmin && <AdminPage />}
        {currentPath === '/admin' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/inbound-messages' && isAuthenticated && isAdmin && <InboundMessagesPage />}
        {currentPath === '/admin/inbound-messages' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/inbound-messages' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/media' && isAuthenticated && isAdmin && <MediaPage key={activeEventId} />}
        {currentPath === '/admin/media' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/media' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/knowledge-base' && isAuthenticated && isAdmin && <KnowledgeManagementPage key={activeEventId} />}
        {currentPath === '/admin/knowledge-base' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/knowledge-base' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/delete-event-components' && isAuthenticated && isAdmin && <DeleteEventComponentsPage key={activeEventId} />}
        {currentPath === '/admin/delete-event-components' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/delete-event-components' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/ai-config' && isAuthenticated && isAdmin && <AIConfigPage key={activeEventId} />}
        {currentPath === '/admin/ai-config' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/ai-config' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/ai-test' && isAuthenticated && isAdmin && <AITestPage key={activeEventId} />}
        {currentPath === '/admin/ai-test' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/ai-test' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">Login</a>
          </div>
        )}

        {currentPath === '/admin/events' && isAuthenticated && isAdmin && <EventManagementPage />}
        {currentPath === '/admin/events' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/events' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/templates' && isAuthenticated && isAdmin && <TemplateManagementPage />}
        {currentPath === '/admin/templates' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/templates' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/admin/self-registration' && isAuthenticated && isAdmin && <SelfRegistrationSettingsPage />}
        {currentPath === '/admin/self-registration' && isAuthenticated && !isAdmin && (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
            <p>This page is only available to users in the Admin group.</p>
          </div>
        )}
        {currentPath === '/admin/self-registration' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/events' && isAuthenticated && <EventsPage key={activeEventId} />}
        {currentPath === '/events' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
          </div>
        )}

        {currentPath === '/heydj' && isAuthenticated && <HeyDJPage key={activeEventId} />}
        {currentPath === '/heydj' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">
              Login
            </a>
          </div>
        )}

        {currentPath === '/user-guide' && isAuthenticated && <UserGuidePage />}
        {currentPath === '/user-guide' && !isAuthenticated && (
          <div className="login-prompt">
            <p>Please log in to view this page.</p>
            <a href={CONFIG.cognitoLoginUrl} className="login-button">Login</a>
          </div>
        )}

        {currentPath === '/terms' &&  <TermsPage />}
        {currentPath === '/privacy' &&  <PrivacyPolicyPage />}
        {(currentPath === '/addme' || currentPath.startsWith('/addme/')) && (
          <AddMePage token={currentPath.startsWith('/addme/') ? currentPath.replace('/addme/', '') : null} />
        )}
        {currentPath.startsWith('/heydj/request/') && (
          <HeyDJRequestPage eventCode={currentPath.replace('/heydj/request/', '')} />
        )}
        {currentPath.startsWith('/heydj/') && !currentPath.startsWith('/heydj/request/') && (
          <HeyDJPublicPage token={currentPath.replace('/heydj/', '')} />
        )}

      </main>

      <Footer />
    </div>
  );
}

export default App;